import type * as l from "@cerulea/lexicon";
import {
  imageLimits,
  Post,
  type PostEmbed,
  type StrongRef,
  type TextFacet,
} from "../../structs.ts";
import { type ActorView, type ChannelView, getProfile, searchActors } from "../../queries.ts";
import { type ComposeIntent, onCompose } from "../compose.ts";
import { type AuthenticatedState, authState, pdsRequest } from "../auth.ts";
import { formatError, publicTransport, RpcClient } from "../api.ts";
import { blobUrl } from "../cdn.ts";
import { formatMebibytes, uploadImage } from "../blobs.ts";
import { compileMarkdown, markdownSource } from "../markdown.ts";
import { enrichFacets, extractMentions } from "../rich-facets.ts";
import { highlight } from "../highlight.ts";
import { ownProfile } from "../own-profile.ts";
import { languageChoices } from "../languages.ts";
import {
  embeddedImages,
  type ImageAttachment,
  type MediaLabel,
  mediaLabel,
  mediaLabels,
  mergeEditEmbed,
  postEmbed,
  withMediaLabel,
} from "../embed.ts";
import { buildEagerPost, saveEagerPost } from "../eager-posts.ts";
import { canCreatePosts, createTid, publishPost, rkeyOf, updatePost } from "../repo.ts";
import { canUploadBlob } from "../permissions.ts";
import { postPathFromUri, type Router } from "../router.ts";
import { config } from "../config.ts";
import { actorLabel, initial } from "./actor.tsx";
import { confirmDialog } from "./confirm.tsx";
import { PostSnippet } from "./post.tsx";
import { Editor, formatKeys, replaceText } from "./editor.tsx";

const appView = new RpcClient(publicTransport(config.appView));

const encoder = new TextEncoder();
const segmenter = new Intl.Segmenter("und", { granularity: "grapheme" });
const maxLength = Post.record.properties.text.maxLength!;
const maxGraphemes = Post.record.properties.text.maxGraphemes!;
const languageKey = "cerulea.postLanguage";

interface Attachment {
  readonly id: number;
  readonly file: File | undefined;
  readonly preview: string;
  alt: string;
  aspectRatio?: { readonly width: number; readonly height: number };
  status: "uploading" | "ready" | "error";
  blob?: l.BlobValue;
  error?: string | undefined;
  controller?: AbortController;
}

export function Composer(router: Router): Element {
  const slot = <div class="composer-slot"></div> as HTMLElement;
  let rendered = "";
  let controller: AbortController | undefined;
  slot.style.display = "none";
  authState.subscribeImmediate((state) => {
    if (state.status === "authenticated") {
      const key = `${state.did}:${canCreatePosts(state.scopes)}`;
      if (key === rendered) return;
      rendered = key;
      controller?.abort();
      controller = new AbortController();
      slot.replaceChildren(contents(state, controller.signal, router));
      slot.style.removeProperty("display");
    } else if (rendered) {
      controller?.abort();
      rendered = "";
      slot.replaceChildren();
      slot.style.display = "none";
    }
  });
  return slot;
}

function contents(
  state: AuthenticatedState,
  signal: AbortSignal,
  router: Router,
): Element {
  if (!canCreatePosts(state.scopes)) {
    return (
      <aside class="composer-permission">
        Sign out and sign in again to grant permission to create Cerulea posts.
      </aside>
    );
  }

  let target: ComposeIntent | undefined;
  const replyContext = <aside class="reply-context" hidden></aside> as HTMLElement;
  const quoteContext = <aside class="embed quote" hidden></aside> as HTMLElement;
  const channel = (
    <select aria-label="Post channel">
      <option value="">Main</option>
    </select>
  ) as HTMLSelectElement;
  const languages = languageChoices(navigator.languages, navigator.language);
  const language = (
    <select aria-label="Post language">
      {languages.map(({ code, name }) => <option value={code}>{name}</option>)}
    </select>
  ) as HTMLSelectElement;
  const warning = (
    <select aria-label="Content warning">
      <option value="">None</option>
      {Object.entries(mediaLabels).map(([value, name]) => <option value={value}>{name}</option>)}
    </select>
  ) as HTMLSelectElement;
  const warningOption = (
    <label class="composer-option" hidden>
      <span>Content warning</span>
      {warning}
    </label>
  ) as HTMLElement;
  const preferredLanguage = () => {
    let stored: string | null = null;
    try {
      stored = localStorage.getItem(languageKey);
    } catch {
      // Without storage, fall back to the browser's languages.
    }
    return languages.some(({ code }) => code === stored) ? stored! : languages[0]!.code;
  };
  let available = new Set([""]);
  let channelViews: readonly ChannelView[] = [];
  const textarea = (
    <textarea
      name="post"
      maxLength={maxLength}
      placeholder="Write a post…"
      spellcheck
    >
    </textarea>
  ) as HTMLTextAreaElement;
  const suggestBox = <div class="mention-suggest" role="listbox" hidden></div> as HTMLElement;
  const editor = Editor(textarea);
  const attachGrid = <div class="composer-attachments"></div> as HTMLElement;
  const fileInput = (
    <input type="file" accept="image/*" multiple hidden aria-label="Attach images" />
  ) as HTMLInputElement;
  const attachButton = (
    <button
      class="composer-tool"
      type="button"
      aria-label="Attach images"
    >
      <span class="lucide lucide-image" aria-hidden="true"></span>
    </button>
  ) as HTMLButtonElement;
  const modifier = /Mac|iPhone|iPad/.test(navigator.userAgent) ? "⌘" : "Ctrl+";
  const formatButtons = (["bold", "italic", "link"] as const).map((kind) => {
    const label = kind[0]!.toUpperCase() + kind.slice(1);
    const button = (
      <button
        class="composer-tool"
        type="button"
        aria-label={label}
        title={`${label} (${modifier}${formatKeys[kind].toUpperCase()})`}
      >
        <span class={`lucide lucide-${kind}`} aria-hidden="true"></span>
      </button>
    ) as HTMLButtonElement;
    // Keep the textarea's focus, and with it the selection being formatted.
    button.addEventListener("mousedown", (event) => event.preventDefault());
    button.addEventListener("click", () => editor.format(kind));
    return button;
  });
  const ring = <span class="composer-ring" aria-hidden="true"></span> as HTMLElement;
  const remaining = <span></span>;
  const count = <span class="composer-count" hidden>{ring}{remaining}</span> as HTMLElement;
  const status = <div class="composer-status" role="status"></div>;
  const publish = (
    <button class="composer-publish" type="submit" disabled>
      Publish
    </button>
  ) as HTMLButtonElement;
  let compiled = compileMarkdown("");
  let enriched: readonly TextFacet[] = [];
  let pending: {
    readonly source: string;
    readonly target: ComposeIntent | undefined;
    readonly rkey: string;
    readonly sig: string;
    readonly record: Parameters<typeof publishPost>[0];
  } | undefined;
  let tooLong = false;
  let publishing = false;

  let attachments: Attachment[] = [];
  let nextAttachment = 1;
  const dids = new Map<string, `did:${string}` | undefined>();
  let resolveTimer: ReturnType<typeof setTimeout> | undefined;
  let resolveFlight: AbortController | undefined;
  let resolving = false;
  let suggesting = false;
  let suggest: { token: string; items: readonly ActorView[]; active: number } | undefined;
  let suggestFlight: AbortController | undefined;
  let suggestTimer: ReturnType<typeof setTimeout> | undefined;

  const setResolving = (value: boolean) => {
    if (resolving === value) return;
    resolving = value;
    update();
  };

  const setSuggesting = (value: boolean) => {
    if (suggesting === value) return;
    suggesting = value;
    update();
  };

  const readyAttachments = (): ImageAttachment[] =>
    attachments.flatMap((attachment): ImageAttachment[] =>
      attachment.status === "ready" && attachment.blob
        ? [{
          blob: attachment.blob,
          alt: attachment.alt.trim(),
          ...(attachment.aspectRatio && { aspectRatio: attachment.aspectRatio }),
        }]
        : []
    );

  const quoteSubject = (): StrongRef | undefined =>
    target?.kind === "quote"
      ? { uri: target.post.uri as `at://${string}`, cid: target.post.cid }
      : undefined;

  // What a failed publish must still match for its retry to reuse the same rkey.
  const signature = (embed: PostEmbed | undefined) =>
    JSON.stringify({
      facets: enriched,
      embed: embed ?? null,
      langs: language.value,
      labels: chosenLabel() ?? null,
    });

  // Labels warn about media, so they only apply while the post has some.
  const chosenLabel = (): MediaLabel | undefined =>
    attachments.length && warning.value ? warning.value as MediaLabel : undefined;

  const buildEmbed = (): PostEmbed | undefined => {
    if (target?.kind === "edit") {
      return mergeEditEmbed(target.post.record.embed, readyAttachments());
    }
    return postEmbed(readyAttachments(), quoteSubject());
  };

  const update = () => {
    compiled = compileMarkdown(textarea.value);
    enriched = enrichFacets(compiled.text, compiled.facets, dids);
    const graphemes = [...segmenter.segment(compiled.text)].length;
    const bytes = encoder.encode(compiled.text).length;
    tooLong = graphemes > maxGraphemes || bytes > maxLength;
    // Posts can be very long, so the limit only shows once it is close.
    const used = Math.max(graphemes / maxGraphemes, bytes / maxLength);
    count.hidden = used < 0.9;
    count.classList.toggle("too-long", tooLong);
    count.title = `${graphemes.toLocaleString()} / ${maxGraphemes.toLocaleString()} characters`;
    ring.style.setProperty("--used", String(Math.min(used, 1)));
    remaining.textContent = bytes / maxLength > graphemes / maxGraphemes
      ? `${(maxLength - bytes).toLocaleString()} bytes`
      : (maxGraphemes - graphemes).toLocaleString();
    const busy = attachments.some((attachment) => attachment.status === "uploading") ||
      resolving || suggesting;
    const hasContent = compiled.text.trim().length > 0 ||
      attachments.some((attachment) => attachment.status === "ready" && attachment.blob);
    publish.disabled = publishing || busy || tooLong || !hasContent ||
      !available.has(channel.value);
    channel.disabled = publishing;
    warningOption.hidden = !attachments.length;
    const sig = signature(buildEmbed());
    if (
      pending?.source !== textarea.value ||
      pending?.record.channel !== (channel.value || undefined) ||
      pending?.target !== target ||
      pending?.sig !== sig
    ) pending = undefined;
    editor.render(highlight(textarea.value, compiled, enriched));
    saveDraft();
  };

  let dragged: Attachment | undefined;
  const renderAttachments = () => {
    attachGrid.replaceChildren(...attachments.map((attachment, index) => {
      const label = `image ${index + 1}`;
      const thumb = attachment.preview
        ? <img class="attachment-thumb" src={attachment.preview} alt="" draggable={false} />
        : <span class="attachment-thumb attachment-missing" aria-hidden="true">{label}</span>;
      const alt = (
        <button
          class={attachment.alt ? "attachment-alt has-alt" : "attachment-alt"}
          type="button"
          aria-label={`${attachment.alt ? "Edit" : "Add"} alt text for ${label}`}
        >
          {attachment.alt ? "ALT ✓" : "+ ALT"}
        </button>
      ) as HTMLButtonElement;
      alt.addEventListener("click", () => editAlt(attachment));
      const remove = (
        <button class="attachment-remove" type="button" aria-label={`Remove ${label}`}>
          ×
        </button>
      ) as HTMLButtonElement;
      remove.addEventListener("click", () => {
        attachment.controller?.abort();
        if (attachment.file) URL.revokeObjectURL(attachment.preview);
        attachments = attachments.filter((other) => other !== attachment);
        renderAttachments();
        update();
      });
      let state: Element | undefined;
      if (attachment.status === "uploading") {
        state = <span class="attachment-state">Uploading…</span>;
      } else if (attachment.status === "error") {
        const retry = <button type="button">Retry</button> as HTMLButtonElement;
        retry.addEventListener("click", () => {
          attachment.status = "uploading";
          attachment.error = undefined;
          renderAttachments();
          update();
          startUpload(attachment);
        });
        state = (
          <span class="attachment-state attachment-error">
            <span>{attachment.error || "Upload failed"}</span>
            {retry}
          </span>
        );
      }
      const tile = (
        <div class="attachment" draggable title="Drag to reorder">
          {thumb}
          {state}
          {alt}
          {remove}
        </div>
      ) as HTMLElement;
      tile.addEventListener("dragstart", (event) => {
        dragged = attachment;
        // A type of our own, so the file drop zone ignores this drag.
        event.dataTransfer?.setData("application/x-cerulea-attachment", String(attachment.id));
      });
      tile.addEventListener("dragend", () => {
        dragged = undefined;
      });
      tile.addEventListener("dragover", (event) => {
        if (!dragged || dragged === attachment) return;
        event.preventDefault();
        tile.classList.add("drop-target");
      });
      tile.addEventListener("dragleave", () => tile.classList.remove("drop-target"));
      tile.addEventListener("drop", (event) => {
        if (!dragged || dragged === attachment) return;
        event.preventDefault();
        const moved = dragged;
        const to = attachments.indexOf(attachment);
        attachments = attachments.filter((other) => other !== moved);
        attachments.splice(to, 0, moved);
        renderAttachments();
        update();
      });
      return tile;
    }));
  };

  const altPreview = <img class="alt-preview" alt="" /> as HTMLImageElement;
  const altText = (
    <textarea
      aria-label="Alt text"
      maxLength={10_000}
      rows={4}
      placeholder="Describe the image for people who can't see it"
    >
    </textarea>
  ) as HTMLTextAreaElement;
  const altDialog = (
    <dialog class="alt-dialog" aria-labelledby="alt-title">
      <form method="dialog" class="alt-body">
        <h2 id="alt-title">Alt text</h2>
        {altPreview}
        {altText}
        <div class="confirm-actions">
          <button type="submit" value="cancel">Cancel</button>
          <button type="submit" value="save">Save</button>
        </div>
      </form>
    </dialog>
  ) as HTMLDialogElement;
  let altTarget: Attachment | undefined;
  const editAlt = (attachment: Attachment) => {
    altTarget = attachment;
    altPreview.src = attachment.preview;
    altPreview.hidden = !attachment.preview;
    altText.value = attachment.alt;
    altDialog.returnValue = "";
    altDialog.showModal();
  };
  altDialog.addEventListener("click", (event) => {
    if (event.target === altDialog) altDialog.close();
  });
  altDialog.addEventListener("close", () => {
    if (altDialog.returnValue === "save" && altTarget) {
      altTarget.alt = altText.value;
      renderAttachments();
      update();
    }
    altTarget = undefined;
  });

  const startUpload = (attachment: Attachment) => {
    const file = attachment.file;
    if (!file) return;
    if (typeof createImageBitmap === "function") {
      void createImageBitmap(file).then((bitmap) => {
        attachment.aspectRatio = { width: bitmap.width, height: bitmap.height };
        bitmap.close();
      }).catch(() => {});
    }
    const controller = new AbortController();
    attachment.controller = controller;
    void uploadImage(
      pdsRequest,
      file,
      imageLimits.bytes,
      controller.signal,
    ).then((blob) => {
      if (controller.signal.aborted) return;
      attachment.status = "ready";
      attachment.blob = blob;
      renderAttachments();
      update();
    }).catch((error) => {
      if (controller.signal.aborted) return;
      attachment.status = "error";
      attachment.error = error instanceof TypeError ? error.message : formatError(error);
      renderAttachments();
      update();
    });
  };

  const canAttach = () => canUploadBlob(state.scopes, "image/png");

  const addFiles = (files: Iterable<File>) => {
    if (!canAttach()) {
      status.textContent = "Authorize image uploads first";
      return;
    }
    let added = false;
    for (const file of files) {
      if (attachments.length >= imageLimits.count) {
        status.textContent = `Posts hold at most ${imageLimits.count} images`;
        break;
      }
      if (!file.type.startsWith("image/")) {
        status.textContent = `Skipped ${file.name || "a file"}: only images can be attached`;
        continue;
      }
      if (file.size > imageLimits.bytes) {
        status.textContent = `Skipped ${file.name || "an image"}: images must be no larger than ${
          formatMebibytes(imageLimits.bytes)
        }`;
        continue;
      }
      const attachment: Attachment = {
        id: nextAttachment++,
        file,
        preview: URL.createObjectURL(file),
        alt: "",
        status: "uploading",
      };
      attachments.push(attachment);
      startUpload(attachment);
      added = true;
    }
    if (added) {
      renderAttachments();
      update();
    }
  };

  const resolveHandle = async (
    handle: string,
    signal: AbortSignal,
  ): Promise<`did:${string}` | undefined> => {
    try {
      const page = await appView.call(searchActors, {
        parameters: { q: handle, limit: 10 },
        signal,
      });
      const exact = page.actors.find((actor) => actor.handle?.toLowerCase() === handle);
      if (exact) return exact.did;
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") throw error;
    }
    const view = await appView.call(getProfile, { parameters: { actor: handle }, signal });
    return view.actor.did;
  };

  const resolveNow = async (text: string, base: readonly TextFacet[]): Promise<boolean> => {
    const wanted = new Set(
      extractMentions(text, base.map((facet) => ({ start: facet.start, end: facet.end })))
        .map((mention) => mention.handle.toLowerCase()),
    );
    let changed = false;
    resolveFlight?.abort();
    const flight = new AbortController();
    resolveFlight = flight;
    setResolving(true);
    try {
      for (const handle of wanted) {
        if (dids.has(handle) || flight.signal.aborted) continue;
        try {
          const did = await resolveHandle(handle, flight.signal);
          if (flight.signal.aborted) return changed;
          dids.set(handle, did);
          if (did) changed = true;
        } catch (error) {
          if (flight.signal.aborted) return changed;
          if (error instanceof DOMException && error.name === "AbortError") return changed;
          dids.set(handle, undefined);
        }
      }
      return changed;
    } finally {
      setResolving(false);
    }
  };

  const scheduleResolve = () => {
    clearTimeout(resolveTimer);
    resolveTimer = setTimeout(() => {
      void resolveNow(compiled.text, compiled.facets);
    }, 400);
  };

  const hideSuggest = () => {
    suggest = undefined;
    suggestFlight?.abort();
    setSuggesting(false);
    clearTimeout(suggestTimer);
    suggestBox.hidden = true;
    suggestBox.replaceChildren();
  };

  const caretToken = (): { start: number; token: string } | undefined => {
    if (textarea.selectionStart !== textarea.selectionEnd) return undefined;
    const caret = textarea.selectionStart;
    let start = caret - 1;
    while (start >= 0 && /[A-Za-z0-9.-]/.test(textarea.value[start]!)) start--;
    if (start < 0 || textarea.value[start] !== "@") return undefined;
    if (start > 0 && /[A-Za-z0-9_@]/.test(textarea.value[start - 1]!)) return undefined;
    return { start, token: textarea.value.slice(start + 1, caret) };
  };

  const refreshSuggest = () => {
    const found = caretToken();
    if (!found || !found.token) {
      hideSuggest();
      return;
    }
    clearTimeout(suggestTimer);
    suggestTimer = setTimeout(() => {
      suggestFlight?.abort();
      const flight = new AbortController();
      suggestFlight = flight;
      setSuggesting(true);
      void appView.call(searchActors, {
        parameters: { q: found.token, limit: 6 },
        signal: flight.signal,
      }).then((page) => {
        if (flight.signal.aborted) return;
        const current = caretToken();
        if (!current || current.token !== found.token) {
          setSuggesting(false);
          return;
        }
        const items = page.actors.filter((actor) => actor.handle);
        if (!items.length) {
          hideSuggest();
          return;
        }
        suggest = { token: found.token, items, active: 0 };
        suggestBox.replaceChildren(
          ...items.map((actor, index) => {
            const row = (
              <button
                class={index === 0 ? "suggest-row active" : "suggest-row"}
                type="button"
                role="option"
                aria-selected={index === 0 ? "true" : "false"}
              >
                <span class="search-avatar" aria-hidden="true">
                  {initial(actorLabel(actor))}
                </span>
                <span>
                  <strong>{actorLabel(actor)}</strong>
                  {actor.handle && <small>@{actor.handle}</small>}
                </span>
              </button>
            ) as HTMLButtonElement;
            row.addEventListener("mousedown", (event) => {
              event.preventDefault();
              acceptSuggest(actor);
            });
            return row;
          }),
        );
        suggestBox.hidden = false;
        const at = editor.rect(found.start, found.start + 1);
        const frame = suggestBox.offsetParent?.getBoundingClientRect();
        if (at && frame) {
          suggestBox.style.top = `${at.bottom - frame.top + 4}px`;
          suggestBox.style.left = `${
            Math.max(0, Math.min(at.left - frame.left, frame.width - suggestBox.offsetWidth))
          }px`;
        }
        setSuggesting(false);
      }).catch(() => {
        setSuggesting(false);
        if (!flight.signal.aborted) hideSuggest();
      });
    }, 180);
  };

  const acceptSuggest = (actor: ActorView) => {
    const found = caretToken();
    if (!found || !actor.handle) {
      hideSuggest();
      return;
    }
    dids.set(actor.handle.toLowerCase(), actor.did as `did:${string}`);
    hideSuggest();
    replaceText(textarea, found.start, textarea.selectionStart, `@${actor.handle} `);
  };

  textarea.addEventListener("input", (event) => {
    update();
    scheduleResolve();
    // Suggestions wait for IME composition to commit.
    if (!(event as InputEvent).isComposing) refreshSuggest();
  });
  textarea.addEventListener("compositionend", refreshSuggest);
  textarea.addEventListener("click", refreshSuggest);
  textarea.addEventListener("keydown", (event) => {
    if (event.isComposing) return;
    if ((event.ctrlKey || event.metaKey) && event.key === "Enter") {
      event.preventDefault();
      void submit();
      return;
    }
    if (suggestBox.hidden || !suggest) return;
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      const delta = event.key === "ArrowDown" ? 1 : -1;
      suggest = {
        ...suggest,
        active: (suggest.active + delta + suggest.items.length) % suggest.items.length,
      };
      [...suggestBox.children].forEach((child, index) => {
        child.setAttribute("aria-selected", index === suggest!.active ? "true" : "false");
        child.classList.toggle("active", index === suggest!.active);
      });
    } else if (event.key === "Enter" || event.key === "Tab") {
      const item = suggest.items[suggest.active];
      if (item) {
        event.preventDefault();
        acceptSuggest(item);
      }
    } else if (event.key === "Escape") {
      event.preventDefault();
      hideSuggest();
    }
  });
  textarea.addEventListener("paste", (event) => {
    const files = event.clipboardData?.files;
    if (files?.length) {
      event.preventDefault();
      addFiles([...files].filter((file) => file.size > 0));
    }
  });
  attachButton.addEventListener("click", () => {
    if (!canAttach()) {
      status.textContent = "Authorize image uploads first";
      return;
    }
    fileInput.click();
  });
  fileInput.addEventListener("change", () => {
    if (fileInput.files?.length) addFiles([...fileInput.files]);
    fileInput.value = "";
  });
  channel.addEventListener("change", update);
  warning.addEventListener("change", update);
  language.addEventListener("change", () => {
    try {
      localStorage.setItem(languageKey, language.value);
    } catch {
      // The choice still applies to this post.
    }
    update();
  });

  const form = (
    <form
      class="composer"
      _onsubmit={(event) => {
        event.preventDefault();
        void submit();
      }}
    >
      <label class="sr-only" htmlFor="post-composer">Post text</label>
      {replyContext}
      <div class="composer-toolbar">
        {attachButton}
        {formatButtons}
        {fileInput}
      </div>
      <div class="composer-textwrap">
        {editor.element}
        {suggestBox}
      </div>
      {attachGrid}
      {quoteContext}
      <div class="composer-meta">
        <label class="composer-option">
          <span>Channel</span>
          {channel}
        </label>
        <label class="composer-option">
          <span>Language</span>
          {language}
        </label>
        {warningOption}
      </div>
      {status}
      <div class="composer-actions">
        <span class="composer-spacer"></span>
        {count}
        {publish}
      </div>
    </form>
  ) as HTMLFormElement;
  const dropZone = (
    <div class="composer-drop" hidden>
      <p>Drop images to attach them</p>
    </div>
  ) as HTMLElement;
  textarea.id = "post-composer";
  const close = (
    <button class="composer-close" type="button" aria-label="Close">
      ×
    </button>
  ) as HTMLButtonElement;
  const title = <h2 id="composer-title">New post</h2>;
  const dialog = (
    <dialog class="composer-dialog" aria-labelledby="composer-title">
      <section class="composer-modal">
        <header class="composer-modal-header">
          {title}
          {close}
        </header>
        {form}
      </section>
      {dropZone}
    </dialog>
  ) as HTMLDialogElement;
  // Once shown, the zone covers the window and receives the rest of the drag.
  dialog.addEventListener("dragenter", (event) => {
    if (!event.dataTransfer?.types.includes("Files")) return;
    event.preventDefault();
    dropZone.hidden = false;
  });
  dropZone.addEventListener("dragover", (event) => event.preventDefault());
  dropZone.addEventListener("dragleave", () => {
    dropZone.hidden = true;
  });
  dropZone.addEventListener("drop", (event) => {
    event.preventDefault();
    dropZone.hidden = true;
    const files = event.dataTransfer?.files;
    if (files?.length) addFiles([...files]);
  });
  const open = <button class="composer-open" type="button">New post</button> as HTMLButtonElement;

  const resetContexts = () => {
    replyContext.hidden = true;
    replyContext.replaceChildren();
    quoteContext.hidden = true;
    quoteContext.replaceChildren();
  };

  const cleanupAttachments = () => {
    for (const attachment of attachments) {
      attachment.controller?.abort();
      if (attachment.file) URL.revokeObjectURL(attachment.preview);
    }
    attachments = [];
  };

  const draftKey = "cerulea.draft";

  const saveDraft = () => {
    if (target) return;
    try {
      if (!textarea.value && !attachments.length) localStorage.removeItem(draftKey);
      else {
        localStorage.setItem(
          draftKey,
          JSON.stringify({ text: textarea.value, channel: channel.value }),
        );
      }
    } catch {
      // Private mode and quotas: drafts simply do not persist.
    }
  };

  const clearDraft = () => {
    try {
      localStorage.removeItem(draftKey);
    } catch {
      // Already gone.
    }
  };

  const resetComposer = (keepText = false) => {
    if (!keepText) textarea.value = "";
    language.value = preferredLanguage();
    warning.value = "";
    cleanupAttachments();
    renderAttachments();
    resetContexts();
    nextAttachment = 1;
    target = undefined;
    title.textContent = "New post";
    publish.textContent = "Publish";
    textarea.placeholder = "Write a post…";
  };

  const dismiss = () => {
    if (textarea.value.length === 0 && attachments.length === 0) {
      resetComposer();
      update();
      dialog.close();
      return;
    }
    void confirmDialog("Discard this post?", "Discard").then((confirmed) => {
      if (!confirmed) return;
      resetComposer();
      update();
      dialog.close();
    });
  };

  const submit = async () => {
    if (publishing || tooLong || !available.has(channel.value)) return;
    if (attachments.some((attachment) => attachment.status === "uploading")) return;
    clearTimeout(resolveTimer);
    hideSuggest();
    publishing = true;
    status.textContent = target?.kind === "edit" ? "Saving…" : "Publishing…";
    update();
    const fresh = compileMarkdown(textarea.value);
    for (const mention of extractMentions(fresh.text, fresh.facets)) {
      const key = mention.handle.toLowerCase();
      if (dids.has(key) && dids.get(key) === undefined) dids.delete(key);
    }
    await resolveNow(fresh.text, fresh.facets);
    if (!dialog.open || signal.aborted) {
      publishing = false;
      return;
    }
    update();
    const source = textarea.value;
    const ready = readyAttachments();
    if (!compiled.text.trim() && !ready.length) {
      publishing = false;
      status.textContent = "";
      update();
      return;
    }
    if (target?.kind === "edit") {
      const { post, onSaved } = target;
      const embed = mergeEditEmbed(post.record.embed, ready);
      const { channel: _channel, embed: _embed, labels: _labels, ...kept } = post.record;
      // Without images in the composer there is no warning to change, e.g. on a video post.
      const labels = attachments.length
        ? withMediaLabel(post.record.labels, chosenLabel())
        : post.record.labels;
      const record = {
        ...kept,
        ...(channel.value && { channel: channel.value }),
        ...(embed && { embed }),
        langs: language.value === post.record.langs?.[0] ? post.record.langs : [language.value],
        ...(labels && { labels }),
        text: compiled.text,
        facets: [...enriched],
        editedAt: new Date().toISOString(),
      };
      try {
        const result = await updatePost(post, record);
        publishing = false;
        status.textContent = "";
        resetComposer(textarea.value !== source);
        dialog.close();
        const updated = { ...post, cid: result.cid, record };
        saveEagerPost(updated);
        onSaved(updated);
      } catch (error) {
        publishing = false;
        status.textContent = formatError(error);
        update();
      }
      return;
    }
    const subject = target && { uri: target.post.uri as `at://${string}`, cid: target.post.cid };
    const embed = buildEmbed();
    const labels = withMediaLabel(undefined, chosenLabel());
    // update() just dropped any pending intent that no longer matches the inputs.
    const intent = pending ?? {
      source,
      target,
      rkey: createTid(),
      sig: signature(embed),
      record: {
        $type: "blue.cerulea.app.post",
        ...(channel.value && { channel: channel.value }),
        ...(target?.kind === "reply" && {
          reply: { root: target.post.record.reply?.root ?? subject!, parent: subject! },
        }),
        ...(embed && { embed }),
        langs: [language.value],
        ...(labels && { labels }),
        text: compiled.text,
        facets: [...enriched],
        createdAt: new Date().toISOString(),
      } as const,
    };
    const channelView = channel.value
      ? channelViews.find((entry) => rkeyOf(entry.uri) === channel.value)
      : undefined;
    try {
      const result = await publishPost(intent.record, intent.rkey);
      pending = undefined;
      saveEagerPost(buildEagerPost({
        uri: result.uri,
        cid: result.cid,
        // The cached profile lends the local copy a recognisable author.
        author: ownProfile.get()?.actor.did === state.did
          ? ownProfile.get()!.actor
          : { did: state.did as `did:${string}` },
        record: intent.record,
        ...(target?.kind === "reply" && { replyTo: target.post.author }),
        ...(channelView && { channel: channelView }),
      }));
      resetComposer(textarea.value !== source);
      clearDraft();
      publishing = false;
      update();
      status.textContent = "";
      dialog.close();
      const path = postPathFromUri(result.uri);
      if (path && !signal.aborted) router.navigate(path);
    } catch (error) {
      pending = intent;
      publishing = false;
      status.textContent = formatError(error);
      update();
    }
  };

  const showChannels = (channels: readonly ChannelView[]) => {
    channelViews = channels;
    const selected = channel.value;
    available = new Set(["", ...channels.map((entry) => rkeyOf(entry.uri))]);
    channel.replaceChildren(<option value="">Main</option>);
    for (const entry of channels) {
      channel.append(<option value={rkeyOf(entry.uri)}>{entry.record.name}</option>);
    }
    if (!available.has(selected)) {
      channel.append(<option value={selected}>Unavailable channel — choose another</option>);
    }
    channel.value = selected;
  };

  const openComposer = (intent?: ComposeIntent) => {
    clearTimeout(resolveTimer);
    resolveFlight?.abort();
    hideSuggest();
    fileInput.value = "";
    resetComposer();
    attachButton.title = canAttach()
      ? "Attach images"
      : "Sign out and sign in again to attach images";
    if (intent) {
      target = intent;
      const reply = intent.kind === "reply";
      const titles = { reply: "Reply", quote: "Quote post", edit: "Edit post" };
      title.textContent = titles[intent.kind];
      publish.textContent = reply ? "Reply" : intent.kind === "edit" ? "Save" : "Publish";
      textarea.placeholder = reply ? "Post your reply…" : "Write a post…";
      if (intent.kind === "edit") {
        textarea.value = markdownSource(intent.post.record.text, intent.post.record.facets);
        attachments = embeddedImages(intent.post.record.embed).map((item) => ({
          id: nextAttachment++,
          file: undefined,
          preview: blobUrl(config.cdn, intent.post.author.did, item.image.ref.$link) ?? "",
          alt: item.alt ?? "",
          ...(item.aspectRatio && {
            aspectRatio: { width: item.aspectRatio.width, height: item.aspectRatio.height },
          }),
          status: "ready" as const,
          blob: item.image,
        }));
        renderAttachments();
        const key = intent.post.record.channel ?? "";
        if (key && !available.has(key)) {
          channel.append(<option value={key}>{intent.post.channel?.record.name ?? key}</option>);
        }
        channel.value = key;
        const lang = intent.post.record.langs?.[0];
        if (lang && !languages.some(({ code }) => code === lang)) {
          language.append(<option value={lang}>{lang}</option>);
        }
        if (lang) language.value = lang;
        warning.value = mediaLabel(intent.post.record.labels) ?? "";
      } else {
        textarea.value = "";
      }
      attachButton.disabled = !canAttach();
      replyContext.hidden = !reply;
      quoteContext.hidden = intent.kind !== "quote";
      replyContext.replaceChildren();
      quoteContext.replaceChildren();
      if (reply) {
        channel.value = "";
        replyContext.append(PostSnippet(intent.post));
      }
      if (intent.kind === "quote") quoteContext.append(PostSnippet(intent.post));
    } else {
      try {
        const stored: unknown = JSON.parse(localStorage.getItem(draftKey) ?? "null");
        if (stored !== null && typeof stored === "object") {
          const draft = stored as { text?: unknown; channel?: unknown };
          if (typeof draft.text === "string" && draft.text) {
            textarea.value = draft.text;
            if (typeof draft.channel === "string" && draft.channel) {
              channel.value = draft.channel;
            }
          }
        }
      } catch {
        // A corrupt draft is the same as no draft.
      }
    }
    status.textContent = "";
    if (!dialog.open) dialog.showModal();
    textarea.focus();
    showChannels(ownProfile.get()?.channels ?? []);
    update();
  };
  open.addEventListener("click", () => openComposer());
  const channelSubscription = ownProfile.subscribe((view) => {
    if (!dialog.open) return;
    showChannels(view?.channels ?? []);
    update();
  });
  signal.addEventListener("abort", () => channelSubscription.unsubscribe(), { once: true });
  onCompose(openComposer, signal);
  dialog.addEventListener("close", () => {
    resolveFlight?.abort();
    clearTimeout(resolveTimer);
    hideSuggest();
  });
  close.addEventListener("click", () => dismiss());
  dialog.addEventListener("cancel", (event) => {
    event.preventDefault();
    dismiss();
  });
  dialog.addEventListener("click", (event) => {
    if (event.target === dialog) dismiss();
  });
  update();
  return <div class="composer-control">{open}{dialog}{altDialog}</div>;
}
